import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import NavigationBar from 'apollo-react/components/NavigationBar';
import Avatar from 'apollo-react/components/Avatar';
import Typography from '../StyledTypography';
import Box from 'apollo-react/components/Box';
import User from 'apollo-react-icons/User';
import Globe from 'apollo-react-icons/Globe';
import Brick from 'apollo-react-icons/Brick';
import IQVIALogo from '../../../assets/images/IQVIA-logo.png';
import BackgroundWaves from '../../../assets/images/background-waves.png';
import useNotifications from '../../../hooks/api/notifications/useNotifications';
import { getCurrentUser, logout } from '../../../services/authService';
import { EXTERNAL_MY_ORG_PRIVILEGES } from '../../../constants/privilege.constant';

// Fallback theme tokens for IDS theme
const defaultIdsTokens = {
  listItem: {
    default: {
      color: {
        text: {
          sub: {
            enabled: '#FFFFFF', // Default gray text color
          },
        },
      },
    },
  },
  navbar: {
    accent: {
      color: {
        content: {
          item: {
            enabled: '#FFFFFF', // White text for navbar
          },
        },
      },
    },
  },
};

const Header = () => {
  const idsTokens = defaultIdsTokens;
  const [variant] = useState('accent');
  const [hideProfile] = useState(false);
  const { t, i18n } = useTranslation();
  const [userProfile, setUserProfile] = useState(null);
  const [privileges, setPrivileges] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await getCurrentUser();
        if (!response?.user) return;

        const { id, username, Role, category, email, is_internal } =
          response.user;
        const persona = is_internal ? 'user' : 'endUser';
        const title = is_internal ? Role?.name : category?.name;

        setUserProfile({
          id,
          name: username,
          title,
          email,
          icon: (
            <Avatar size="small" alt={username} stateless>
              {username
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </Avatar>
          ),
          persona,
        });
        if (response.allPrivileges) {
          setPrivileges(response.allPrivileges);
        }
        if (response?.organizationId) {
          setOrganizationId(response.organizationId);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();

    const handleProfileUpdate = () => fetchProfileData();
    window.addEventListener('profile-updated', handleProfileUpdate);
    return () =>
      window.removeEventListener('profile-updated', handleProfileUpdate);
  }, []);

  const userId = userProfile?.id;

  const { notifications, markAsRead, deleteNotification } =
    useNotifications(userId);

  const toggleLanguage = useCallback(() => {
    const currentLang = i18n.language;
    const newLang = currentLang === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  }, [i18n]);

  const checkPrivilege = useCallback(
    (privilegeName) => {
      return privileges?.some((priv) => priv.Privilege.name === privilegeName);
    },
    [privileges]
  );

  const showOrgItem =
    userProfile?.persona === 'endUser' &&
    checkPrivilege(EXTERNAL_MY_ORG_PRIVILEGES.VIEW_MY_ORG) &&
    organizationId;

  const handleProfileClick = useCallback(() => {
    if (userProfile?.persona === 'endUser') {
      window.location.href = '/customer/profile';
    } else if (userProfile?.persona === 'user') {
      window.location.href = '/admin/profile';
    }
  }, [userProfile?.persona]);

  const handleOrganizationClick = useCallback(() => {
    window.location.href = `/customer/organization/${organizationId}`;
  }, [organizationId]);

  const menuItems = useMemo(() => {
    const items = [
      {
        text: t('profileData.myProfile'),
        icon: User,
        onClick: handleProfileClick,
      },
    ];

    if (showOrgItem) {
      items.push({
        text: t('organization'),
        icon: Brick,
        onClick: handleOrganizationClick,
      });
    }

    items.push({
      text: i18n.language === 'en' ? 'عربي' : 'English',
      icon: Globe,
      onClick: toggleLanguage,
    });

    return items;
  }, [
    t,
    showOrgItem,
    i18n.language,
    handleProfileClick,
    handleOrganizationClick,
    toggleLanguage,
  ]);

  const profileMenuProps = userProfile
    ? {
        name: hideProfile ? '' : userProfile.name,
        title: userProfile.title,
        email: userProfile.email,
        icon: userProfile.icon,
        id: userProfile.id != null ? String(userProfile.id) : userProfile.id,
        logoutText: t('sign_out'),
        logoutButtonProps: {
          onClick: () => logout?.(),
        },
        extraContent: (
          <Typography
            component="p"
            variant="caption"
            align="center"
            color={
              idsTokens?.listItem?.default?.color?.text?.sub?.enabled ||
              '#666666'
            }
            sx={{ marginBottom: '8px' }}
          >
            v3.0.2 February 04, 2026
          </Typography>
        ),
        menuItems,
      }
    : undefined;

  return (
    <>
      <NavigationBar
        style={{
          background:
            'url(/static/media/waves.a2a40f0467a566181534cfa81efbbb4d.svg),linear-gradient(to right, #250056, #0076AE), url(../../../assets/images/background-waves.png)',
          backgroundRepeat: 'no-repeat',
          height: '56px',
          justifyContent: 'space-between',
        }}
        LogoComponent={() => (
          <Fragment>
            <img
              src={IQVIALogo}
              width="105.54px"
              height="18.91px"
              alt="IQVIA"
              style={{ marginRight: 24 }}
            />
            <Typography
              style={{
                color:
                  idsTokens?.navbar?.[variant]?.color?.content?.item?.enabled ||
                  '#FFFFFF',
                lineHeight: '56px',
                marginRight: 24,
                zIndex: 2,
                whiteSpace: 'nowrap',
              }}
            >
              Conveyor
            </Typography>
          </Fragment>
        )}
        profileMenuProps={profileMenuProps}
        maxWidth={'100%'}
        notificationsMenuProps={{
          notificationIdField: 'id',
          notifications,
          onToggleRead: markAsRead,
          onDelete: deleteNotification,
          onClick: markAsRead,
          onButtonClick: () => {},
          emptyText: (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="bodyDefault" emphasis="high" color="#333">
                {'No notifications right now!'}
              </Typography>
              <Typography variant="bodySmall" color="#333">
                {'All squeaky clean.'}
              </Typography>
            </Box>
          ),
        }}
      />
      <img
        src={BackgroundWaves}
        width="226px"
        height="29px"
        alt=""
        style={{ position: 'fixed', top: '56px', right: 0 }}
      />
    </>
  );
};

export default Header;
